import { useMemo } from "react";
import type { CompanyFilter } from "../../../legacy/types/companyFilter";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import { EXPORT_INCLUSION_TYPES, type ExtraSearchFilters } from "../types";

type FaiIdType = "departmentIds" | "roleIds";

const getFaiIdsFromFilters = (
  filters: CompanyFilter | undefined,
  idType: FaiIdType
): string[] => [
  ...(filters?.productCategories?.[idType] ?? []),
  ...(filters?.products ?? []).flatMap((p) => p?.[idType] ?? []),
  ...(filters?.vendors ?? []).flatMap((v) => v?.[idType] ?? []),
];

/**
 * The FAI refinements OppGen adds to an export's company filters
 * (`getSearchParamsWithFaiExportOptions`): nothing while FAI is excluded,
 * otherwise the departments/roles of the install filters — a department with
 * roles expands into them — and the Decision Makers flag.
 */
export const useOppGenFaiExportFilters = (
  faiInclusion: EXPORT_INCLUSION_TYPES,
  filters: CompanyFilter | undefined,
  includeDecisionMakersAndInfluencersOnly: boolean
): ExtraSearchFilters => {
  const { faiDepartmentRolesMap } = useTaxonomyContext();

  return useMemo(() => {
    if (faiInclusion === EXPORT_INCLUSION_TYPES.NONE) return {};

    const roleIds = getFaiIdsFromFilters(filters, "roleIds");
    const departmentIds: string[] = [];

    for (const departmentId of getFaiIdsFromFilters(filters, "departmentIds")) {
      const departmentRoles = faiDepartmentRolesMap.get(departmentId) ?? [];
      if (departmentRoles.length > 0) {
        roleIds.push(...departmentRoles);
      } else {
        departmentIds.push(departmentId);
      }
    }

    return {
      roleIds: roleIds.length === 0 ? undefined : [...new Set(roleIds)],
      departmentIds:
        departmentIds.length === 0 ? undefined : [...new Set(departmentIds)],
      includeDecisionMakersAndInfluencersOnly,
    };
  }, [
    faiInclusion,
    filters,
    includeDecisionMakersAndInfluencersOnly,
    faiDepartmentRolesMap,
  ]);
};
